// @flow
import * as React from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import type { CurrentUser } from 'api/currentUser';
import { promoteResumeDatabase } from 'utils/resumeDatabase';
import { trackClicked } from 'utils/analytics';
import ResumeDatabaseResponseContainer from 'containers/ResumeDatabaseResponseContainer';
import Text, { H5, BodyMicro } from 'components/Text';
import Icon from 'components/Icon';
import Box from 'components/Box';
import Button, { SecondaryButtonRouterLink } from 'components/Button';
import { showModal } from '../redux/main/actions/modal';
import { gutterWidth, colors, transition } from '../theme';

const CloseButton = styled(Button)`
  display: block;
  opacity: 0.7;
  transition: all ${transition};
  &:hover {
    opacity: 1;
  }
`;

const ResumeDatabaseSidebarAd = ({
  currentUser
}: {
  currentUser: CurrentUser
}) => {
  const dispatch = useDispatch();

  const redirectTo = '/dashboard/application-history';

  const showSignUpModal = () => {
    dispatch(showModal('SIGNUP', { redirectTo }));
  };

  return promoteResumeDatabase({
    currentUser,
    allowLoggedOut: true
  }) ? (
    <Box px={[gutterWidth / 2, null, null, 0]}>
      <Box
        position="relative"
        overflow="hidden"
        bg={colors.waterBlue}
        borderRadius="8px"
        color="white"
        mb={32}
      >
        <Box
          position="absolute"
          left={0}
          top={0}
          width={1}
          height="100%"
          backgroundImage="url('/static/images/resume-database-ad.jpg')"
          backgroundSize="cover"
          backgroundPosition="center"
          opacity="0.33"
        />
        <Box position="relative" width={1} p={32}>
          <H5 mb={10} lineHeight={1.375}>
            {/* TRANSLATORS: Resume database promotional banner */ getText(
              'Organizations are looking for someone just like you!'
            )}
          </H5>
          <BodyMicro lineHeight={1.375}>
            {/* TRANSLATORS: Resume database promotional banner */ getText(
              `Get discovered today.`
            )}
          </BodyMicro>

          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="space-between"
            alignItems="center"
            mx="-6px"
            mt={24}
          >
            <Box p="6px" flex="1 0 auto">
              <SecondaryButtonRouterLink
                width="100%"
                to={redirectTo}
                square
                reverse
                onClick={e => {
                  trackClicked('Add Resume', {
                    context: 'Search Sidebar'
                  });
                  if (!currentUser.id) {
                    e.preventDefault();
                    showSignUpModal();
                  }
                }}
              >
                <Box
                  height={24}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text styleAs="h5" my={0}>
                    {getText('Add Your Resume')}
                  </Text>
                </Box>
              </SecondaryButtonRouterLink>
            </Box>

            <Box p="6px" flex="1 0 auto" display="flex" justifyContent="center">
              <ResumeDatabaseResponseContainer>
                {({ dismiss }) => (
                  <CloseButton
                    onClick={() => {
                      dismiss();
                      trackClicked('Dismiss Resume Database CTA', {
                        context: 'Search Sidebar'
                      });
                    }}
                  >
                    <BodyMicro textDecoration="underline">
                      {getText('No, thanks')}
                    </BodyMicro>
                  </CloseButton>
                )}
              </ResumeDatabaseResponseContainer>
            </Box>
          </Box>
        </Box>

        <Box position="absolute" top={12} right={12}>
          <ResumeDatabaseResponseContainer>
            {({ dismiss }) => (
              <CloseButton
                onClick={() => {
                  dismiss();
                  trackClicked('Dismiss Resume Database CTA', {
                    context: 'Search Sidebar'
                  });
                }}
                tabIndex={-1}
              >
                <Icon
                  name="close"
                  display="block"
                  width={12}
                  fill="currentColor"
                />
              </CloseButton>
            )}
          </ResumeDatabaseResponseContainer>
        </Box>
      </Box>
    </Box>
  ) : null;
};

export default ResumeDatabaseSidebarAd;
